import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { userApi, type SubscriptionStatus } from '../services/api';

export function useSubscriptionStatus(revision?: unknown) {
  const { getToken, userId } = useAuth();
  const [state, setState] = useState<{ userId: string | null | undefined; status: SubscriptionStatus | null; error: boolean }>({ userId: undefined, status: null, error: false });
  const [generation, setGeneration] = useState(0);
  const retry = useCallback(() => setGeneration(value => value + 1), []);
  useEffect(() => {
    const abort = new AbortController();
    setState({ userId, status: null, error: false });
    const timer = setTimeout(() => { setState({ userId, status: null, error: true }); abort.abort(); }, 10_000);
    void (async () => {
      try {
        const token = await getToken();
        if (!token || abort.signal.aborted) throw new Error('Authentication required');
        const response = await userApi.getSubscriptionStatus(token, abort.signal);
        if (!response.success) throw new Error('Billing status unavailable');
        if (!abort.signal.aborted) setState({ userId, status: response, error: false });
      } catch { if (!abort.signal.aborted) setState({ userId, status: null, error: true }); }
      finally { clearTimeout(timer); }
    })();
    return () => { clearTimeout(timer); abort.abort(); };
  }, [getToken, userId, revision, generation]);
  return { status: state.userId === userId ? state.status : null, error: state.userId === userId && state.error, retry };
}
